/**
 * Unified Admin Dashboard
 *
 * Central admin interface with tabs for:
 * - Overview (stats)
 * - Users (management + subscriptions)
 * - Support (chat conversations)
 * - Hosting (FluxNode subscriptions)
 * - Modules (permission configuration)
 *
 * Requires ADMIN_KEY for authentication.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Icons } from '../components/Icons';
import { StorageService } from '../services/storage';
import { SupportAdmin } from './SupportAdmin';
import { HostingAdmin } from './HostingAdmin';
import { IntelligenceHubAdmin } from './IntelligenceHubAdmin';
import { BlogAdminPage as BlogAdmin } from './admin/BlogAdminPage';
import './AdminDashboard.css';

// Types
type AdminTab = 'overview' | 'users' | 'support' | 'hosting' | 'intelligence' | 'modules' | 'blog';

interface AdminStats {
  users: {
    total: number;
    recent_signups: number;
  };
  subscriptions: {
    total_active: number;
    by_plan: Record<string, number>;
  };
  onchain: {
    total_wallets: number;
    total_value_usd: number;
  };
}

interface UserWithSubscription {
  id: string;
  email: string;
  username: string | null;
  avatar_url: string | null;
  created_at: string;
  last_sign_in_at: string | null;
  subscription: {
    id: string;
    plan_id: string;
    plan_name: string;
    status: string;
    billing_period: string;
    started_at: string;
    ends_at: string | null;
    vip_source: string | null;
  } | null;
  stats: {
    modules_accessed: number;
    last_activity: string | null;
    onchain_wallets: number;
    total_onchain_value: number;
  };
}

interface ModulePermission {
  id: string;
  module_id: string;
  module_name: string;
  module_description: string | null;
  module_icon: string | null;
  module_category: string;
  is_enabled: boolean;
  requires_auth: boolean;
  min_plan_id: string;
  is_beta: boolean;
  is_coming_soon: boolean;
  display_order: number;
}

// Plan colors
const PLAN_COLORS: Record<string, string> = {
  free: '#10b981',
  starter: '#3b82f6',
  pro: '#8b5cf6',
  expert: '#f59e0b',
  enterprise: '#ef4444',
  vip: '#f59e0b',
  vip_plus: '#ec4899',
};

const PLAN_NAMES: Record<string, string> = {
  free: 'Découverte',
  starter: 'Starter',
  pro: 'Pro',
  expert: 'Baby Whale',
  enterprise: 'Whale',
  vip: 'VIP',
  vip_plus: 'VIP+',
};

export function AdminDashboard() {
  // Auth state
  const [adminKey, setAdminKey] = useState(() =>
    StorageService.get<string>('admin_key', '')
  );
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  // UI state
  const [activeTab, setActiveTab] = useState<AdminTab>('overview');
  const [loading, setLoading] = useState(false);

  // Data state
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [users, setUsers] = useState<UserWithSubscription[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [modules, setModules] = useState<ModulePermission[]>([]);
  const [groupedModules, setGroupedModules] = useState<Record<string, ModulePermission[]>>({});

  // Filters
  const [userSearch, setUserSearch] = useState('');
  const [userPlanFilter, setUserPlanFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState<UserWithSubscription | null>(null);

  // API helper
  const apiCall = useCallback(async (
    action: string,
    params: Record<string, string> = {},
    options: RequestInit = {}
  ) => {
    const queryParams = new URLSearchParams({ action, ...params });
    const response = await fetch(`/api/admin?${queryParams}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminKey}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        setIsAuthenticated(false);
        throw new Error('Non autorisé');
      }
      const error = await response.json();
      throw new Error(error.error || 'Erreur API');
    }

    return response.json();
  }, [adminKey]);

  // Login handler
  const handleLogin = async () => {
    setLoading(true);
    setAuthError(null);

    try {
      await apiCall('stats');
      StorageService.set('admin_key', adminKey);
      setIsAuthenticated(true);
    } catch {
      setAuthError('Clé admin invalide');
    } finally {
      setLoading(false);
    }
  };

  // Load data based on active tab
  const loadData = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    try {
      switch (activeTab) {
        case 'overview':
          const statsData = await apiCall('stats');
          setStats(statsData);
          break;

        case 'users':
          const usersData = await apiCall('users', {
            limit: '50',
            search: userSearch,
            plan: userPlanFilter,
          });
          setUsers(usersData.users || []);
          setUsersTotal(usersData.total || 0);
          break;

        case 'modules':
          const modulesData = await apiCall('modules');
          setModules(modulesData.modules || []);
          setGroupedModules(modulesData.grouped || {});
          break;
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, activeTab, apiCall, userSearch, userPlanFilter]);

  // Auto-load on tab change
  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated, activeTab, loadData]);

  // Try auto-login with stored key
  useEffect(() => {
    if (adminKey && !isAuthenticated) {
      handleLogin();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update module
  const updateModule = async (moduleId: string, updates: Partial<ModulePermission>) => {
    try {
      await apiCall('update-module', {}, {
        method: 'PATCH',
        body: JSON.stringify({ module_id: moduleId, ...updates }),
      });
      await loadData();
    } catch (error) {
      console.error('Error updating module:', error);
    }
  };

  // Create subscription for user
  const createUserSubscription = async (userId: string, planId: string) => {
    try {
      await apiCall('create-subscription', {}, {
        method: 'POST',
        body: JSON.stringify({
          user_id: userId,
          plan_id: planId,
          plan_name: PLAN_NAMES[planId] || planId,
          billing_period: 'manual',
        }),
      });
      await loadData();
      setSelectedUser(null);
    } catch (error) {
      console.error('Error creating subscription:', error);
    }
  };

  // Format date
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Format currency
  const formatUsd = (value: number) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
    return `$${value.toFixed(0)}`;
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="admin-login">
        <div className="admin-login__card">
          <div className="admin-login__header">
            <div className="admin-login__icon">
              <Icons.Shield size={32} />
            </div>
            <h1>Admin Dashboard</h1>
            <p>Entrez votre clé admin pour accéder au panneau d'administration</p>
          </div>

          <input
            type="password"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            placeholder="Clé admin"
            className="admin-login__input"
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
          />

          {authError && (
            <p className="admin-login__error">{authError}</p>
          )}

          <button
            onClick={handleLogin}
            disabled={loading || !adminKey}
            className="admin-login__button"
          >
            {loading ? 'Connexion...' : 'Se connecter'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header__left">
          <Icons.Shield size={28} className="admin-header__icon" />
          <div>
            <h1>Admin Dashboard</h1>
            <p>Gestion centralisée de l'application</p>
          </div>
        </div>
        <button
          onClick={() => {
            setIsAuthenticated(false);
            StorageService.remove('admin_key');
          }}
          className="admin-header__logout"
        >
          <Icons.LogOut size={16} />
          Déconnexion
        </button>
      </header>

      {/* Tabs */}
      <nav className="admin-tabs">
        {[
          { id: 'overview', label: 'Vue d\'ensemble', icon: Icons.BarChart2 },
          { id: 'users', label: 'Utilisateurs', icon: Icons.Users },
          { id: 'support', label: 'Support', icon: Icons.MessageSquare },
          { id: 'hosting', label: 'Hosting', icon: Icons.Server },
          { id: 'intelligence', label: 'Intelligence', icon: Icons.Zap },
          { id: 'blog', label: 'Blog', icon: Icons.FileText },
          { id: 'modules', label: 'Modules', icon: Icons.Layout },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as AdminTab)}
            className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </nav>

      {/* Content */}
      <main className="admin-content">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="admin-overview">
            {loading ? (
              <div className="admin-loading">Chargement...</div>
            ) : stats ? (
              <>
                <div className="admin-stats-grid">
                  <StatCard
                    title="Utilisateurs"
                    value={stats.users.total}
                    icon={<Icons.Users size={24} />}
                    color="#8b5cf6"
                    subtitle={`+${stats.users.recent_signups} cette semaine`}
                  />
                  <StatCard
                    title="Abonnements actifs"
                    value={stats.subscriptions.total_active}
                    icon={<Icons.CreditCard size={24} />}
                    color="#10b981"
                  />
                  <StatCard
                    title="Wallets OnChain"
                    value={stats.onchain.total_wallets}
                    icon={<Icons.Link size={24} />}
                    color="#3b82f6"
                  />
                  <StatCard
                    title="Valeur OnChain"
                    value={formatUsd(stats.onchain.total_value_usd)}
                    icon={<Icons.DollarSign size={24} />}
                    color="#f59e0b"
                  />
                </div>

                <div className="admin-overview__plans">
                  <h2>Répartition par plan</h2>
                  <div className="admin-plans-grid">
                    {Object.entries(PLAN_NAMES).map(([planId, planName]) => (
                      <div
                        key={planId}
                        className="admin-plan-card"
                        style={{ borderColor: PLAN_COLORS[planId] }}
                      >
                        <div
                          className="admin-plan-card__badge"
                          style={{ background: PLAN_COLORS[planId] }}
                        >
                          {planName}
                        </div>
                        <div className="admin-plan-card__value">
                          {stats.subscriptions.by_plan[planId] || 0}
                        </div>
                        <div className="admin-plan-card__label">utilisateurs</div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="admin-users">
            {/* Filters */}
            <div className="admin-users__filters">
              <input
                type="text"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Rechercher par email ou username..."
                className="admin-users__search"
              />
              <select
                aria-label="Filter by plan"
                value={userPlanFilter}
                onChange={(e) => setUserPlanFilter(e.target.value)}
                className="admin-users__filter"
              >
                <option value="all">Tous les plans</option>
                {Object.entries(PLAN_NAMES).map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
              <button onClick={loadData} className="admin-users__refresh">
                <Icons.RefreshCw size={16} />
              </button>
            </div>

            {/* Users list */}
            <div className="admin-users__list">
              {loading ? (
                <div className="admin-loading">Chargement...</div>
              ) : users.length === 0 ? (
                <div className="admin-empty">Aucun utilisateur trouvé</div>
              ) : (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Utilisateur</th>
                      <th>Abonnement</th>
                      <th>Inscrit le</th>
                      <th>Dernière activité</th>
                      <th>Wallets</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td>
                          <div className="admin-user-cell">
                            <div className="admin-user-avatar">
                              {user.avatar_url ? (
                                <img src={user.avatar_url} alt="" />
                              ) : (
                                <Icons.User size={20} />
                              )}
                            </div>
                            <div>
                              <div className="admin-user-name">
                                {user.username || 'Sans nom'}
                              </div>
                              <div className="admin-user-email">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {user.subscription ? (
                            <span
                              className="admin-plan-badge"
                              style={{
                                background: `${PLAN_COLORS[user.subscription.plan_id]}20`,
                                color: PLAN_COLORS[user.subscription.plan_id],
                              }}
                            >
                              {PLAN_NAMES[user.subscription.plan_id] || user.subscription.plan_id}
                              {user.subscription.vip_source && (
                                <Icons.Star size={12} style={{ marginLeft: '4px' }} />
                              )}
                            </span>
                          ) : (
                            <span className="admin-plan-badge admin-plan-badge--free">
                              Free
                            </span>
                          )}
                        </td>
                        <td>{formatDate(user.created_at)}</td>
                        <td>{formatDate(user.stats.last_activity)}</td>
                        <td>
                          {user.stats.onchain_wallets > 0 ? (
                            <span className="admin-wallet-badge">
                              {user.stats.onchain_wallets} ({formatUsd(user.stats.total_onchain_value)})
                            </span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>
                          <button
                            onClick={() => setSelectedUser(user)}
                            className="admin-action-btn"
                          >
                            <Icons.Edit size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="admin-users__total">
              Total: {usersTotal} utilisateurs
            </div>
          </div>
        )}

        {/* Support Tab - Embedded component */}
        {activeTab === 'support' && (
          <div className="admin-embed-component">
            <SupportAdmin adminKey={adminKey} />
          </div>
        )}

        {/* Hosting Tab - Embedded component */}
        {activeTab === 'hosting' && (
          <div className="admin-embed-component">
            <HostingAdmin adminKey={adminKey} />
          </div>
        )}

        {/* Intelligence Hub Tab - Embedded component */}
        {activeTab === 'intelligence' && (
          <div className="admin-embed-component">
            <IntelligenceHubAdmin adminKey={adminKey} />
          </div>
        )}

        {/* Blog Tab - Embedded component */}
        {activeTab === 'blog' && (
          <div className="admin-embed-component">
            <BlogAdmin adminKey={adminKey} />
          </div>
        )}

        {/* Modules Tab */}
        {activeTab === 'modules' && (
          <div className="admin-modules">
            {loading ? (
              <div className="admin-loading">Chargement...</div>
            ) : (
              Object.entries(groupedModules).map(([category, categoryModules]) => (
                <div key={category} className="admin-module-category">
                  <h3 className="admin-module-category__title">
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </h3>
                  <div className="admin-module-grid">
                    {categoryModules.map((mod) => (
                      <div
                        key={mod.id}
                        className={`admin-module-card ${!mod.is_enabled ? 'disabled' : ''}`}
                      >
                        <div className="admin-module-card__header">
                          <div className="admin-module-card__icon">
                            {mod.module_icon && Icons[mod.module_icon as keyof typeof Icons] ? (
                              React.createElement(Icons[mod.module_icon as keyof typeof Icons], { size: 20 })
                            ) : (
                              <Icons.Layout size={20} />
                            )}
                          </div>
                          <div className="admin-module-card__info">
                            <h4>{mod.module_name}</h4>
                            <p>{mod.module_id}</p>
                          </div>
                          <label className="admin-toggle">
                            <input
                              type="checkbox"
                              checked={mod.is_enabled}
                              onChange={(e) => updateModule(mod.module_id, { is_enabled: e.target.checked })}
                            />
                            <span className="admin-toggle__slider" />
                          </label>
                        </div>

                        <div className="admin-module-card__options">
                          <div className="admin-module-option">
                            <label>Auth requise</label>
                            <input
                              type="checkbox"
                              checked={mod.requires_auth}
                              onChange={(e) => updateModule(mod.module_id, { requires_auth: e.target.checked })}
                            />
                          </div>

                          <div className="admin-module-option">
                            <label>Plan minimum</label>
                            <select
                              aria-label="Plan minimum"
                              value={mod.min_plan_id}
                              onChange={(e) => updateModule(mod.module_id, { min_plan_id: e.target.value })}
                            >
                              <option value="free">Free</option>
                              <option value="starter">Starter</option>
                              <option value="pro">Pro</option>
                              <option value="expert">Expert</option>
                              <option value="enterprise">Enterprise</option>
                            </select>
                          </div>

                          <div className="admin-module-option">
                            <label>Beta</label>
                            <input
                              type="checkbox"
                              checked={mod.is_beta}
                              onChange={(e) => updateModule(mod.module_id, { is_beta: e.target.checked })}
                            />
                          </div>

                          <div className="admin-module-option">
                            <label>Coming Soon</label>
                            <input
                              type="checkbox"
                              checked={mod.is_coming_soon}
                              onChange={(e) => updateModule(mod.module_id, { is_coming_soon: e.target.checked })}
                            />
                          </div>
                        </div>

                        <div className="admin-module-card__footer">
                          <span
                            className="admin-module-badge"
                            style={{
                              background: `${PLAN_COLORS[mod.min_plan_id]}20`,
                              color: PLAN_COLORS[mod.min_plan_id],
                            }}
                          >
                            {PLAN_NAMES[mod.min_plan_id] || mod.min_plan_id}
                          </span>
                          {mod.is_beta && (
                            <span className="admin-module-badge admin-module-badge--beta">
                              Beta
                            </span>
                          )}
                          {mod.is_coming_soon && (
                            <span className="admin-module-badge admin-module-badge--soon">
                              Bientôt
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>

      {/* User Edit Modal */}
      {selectedUser && (
        <div className="admin-modal-overlay" onClick={() => setSelectedUser(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal__header">
              <h3>Modifier l'utilisateur</h3>
              <button onClick={() => setSelectedUser(null)}>
                <Icons.X size={20} />
              </button>
            </div>

            <div className="admin-modal__body">
              <div className="admin-modal__user-info">
                <div className="admin-user-avatar admin-user-avatar--large">
                  {selectedUser.avatar_url ? (
                    <img src={selectedUser.avatar_url} alt="" />
                  ) : (
                    <Icons.User size={32} />
                  )}
                </div>
                <div>
                  <h4>{selectedUser.username || 'Sans nom'}</h4>
                  <p>{selectedUser.email}</p>
                  <p className="admin-modal__date">
                    Inscrit le {formatDate(selectedUser.created_at)}
                  </p>
                </div>
              </div>

              <div className="admin-modal__section">
                <h5>Abonnement actuel</h5>
                {selectedUser.subscription ? (
                  <div className="admin-subscription-info">
                    <span
                      className="admin-plan-badge"
                      style={{
                        background: `${PLAN_COLORS[selectedUser.subscription.plan_id]}20`,
                        color: PLAN_COLORS[selectedUser.subscription.plan_id],
                      }}
                    >
                      {PLAN_NAMES[selectedUser.subscription.plan_id]}
                    </span>
                    <span>depuis le {formatDate(selectedUser.subscription.started_at)}</span>
                    {selectedUser.subscription.ends_at && (
                      <span>expire le {formatDate(selectedUser.subscription.ends_at)}</span>
                    )}
                  </div>
                ) : (
                  <p>Aucun abonnement actif (Free)</p>
                )}
              </div>

              <div className="admin-modal__section">
                <h5>Modifier l'abonnement</h5>
                <div className="admin-plan-selector">
                  {Object.entries(PLAN_NAMES).map(([planId, planName]) => (
                    <button
                      key={planId}
                      onClick={() => createUserSubscription(selectedUser.id, planId)}
                      className="admin-plan-select-btn"
                      style={{ borderColor: PLAN_COLORS[planId] }}
                    >
                      <span
                        className="admin-plan-select-badge"
                        style={{ background: PLAN_COLORS[planId] }}
                      >
                        {planName}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="admin-modal__section">
                <h5>Statistiques</h5>
                <div className="admin-user-stats">
                  <div className="admin-user-stat">
                    <Icons.Activity size={16} />
                    <span>{selectedUser.stats.modules_accessed} modules accédés</span>
                  </div>
                  <div className="admin-user-stat">
                    <Icons.Link size={16} />
                    <span>{selectedUser.stats.onchain_wallets} wallets OnChain</span>
                  </div>
                  <div className="admin-user-stat">
                    <Icons.DollarSign size={16} />
                    <span>{formatUsd(selectedUser.stats.total_onchain_value)} total OnChain</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Stat card component
function StatCard({
  title,
  value,
  icon,
  color,
  subtitle,
}: {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
}) {
  return (
    <div className="admin-stat-card" style={{ borderTopColor: color }}>
      <div className="admin-stat-card__icon" style={{ background: `${color}20`, color }}>
        {icon}
      </div>
      <div className="admin-stat-card__content">
        <div className="admin-stat-card__value">{value}</div>
        <div className="admin-stat-card__title">{title}</div>
        {subtitle && <div className="admin-stat-card__subtitle">{subtitle}</div>}
      </div>
    </div>
  );
}

// Need to import React for JSX
import React from 'react';

export default AdminDashboard;
